import { useState, useRef } from "react";
import uploadIcon from '../static/images/upload.png';
import { GreenButton, NoBgButton } from "./buttons";
import Loader from "./Loader";
import ReorderModal from "./ReorderModal";
import { motion } from 'framer-motion';

const UploadPhotos = ({ inputDataGallery, setInputData, coverPublicId, handleSelectCover, type }) => {

    // Handle images upload
    const fileInputRef = useRef(null);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [isSizeError, setIsSizeError] = useState(false);

    const handleUploadImage = (event) => {
        event.preventDefault();

        setIsImageLoading(true);
        const file = event.dataTransfer ? event.dataTransfer.files[0] : event.target.files[0];
        if (file) {
            // Controllo che non ecceda il size dell'immagine
            const fileSize = file.size / (1024 * 1024); // in Mb
            if (fileSize > 9) {
                setIsImageLoading(false);
                setIsSizeError(true);
                setTimeout(() => {
                    inputEmpty();
                    setIsSizeError(false);
                }, 3000)
            } else {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                    setUploadedImage(reader.result);
                    setIsImageLoading(false)
                }
            }
        }
    }

    // Funzione per svuota l'input di upload
    const inputEmpty = () => {
        let inputElement = document.getElementById('fileInput');
        inputElement.value = '';
    }

    // Send image to server
    const [fetchStatus, setFetchStatus] = useState('idle');
    const [fetchError, setFetchError] = useState(null);
    const sendImage = async () => {
        try {
            setFetchStatus('loading');
            const token = localStorage.getItem('ticino-wild-token');
            // Url viene definitio il base alla prop 'type', per impostare la rotta da utilizzare
            // e di conseguenza la cartella Cloudinary in cui salvare l'immagine
            let url;
            if (type === 'Activity') {
                url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/upload-image`;
            }
            if (type === 'Product') {
                url = `${process.env.REACT_APP_SERVER_BASE_URL}/products/upload-image`;
            }
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const options = {
                method: 'POST',
                headers,
                body: JSON.stringify({ image: uploadedImage })
            };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setUploadedImage(null);
                setFetchStatus('succeeded');
                setInputData(prevState => ({
                    ...prevState,
                    gallery: [...prevState.gallery, result.payload]
                }))
                handleOverlay();
            } else {
                const error = await response.json();
                setFetchError('Fetch error: ', error.message);
                setFetchStatus('failed');
            }
        } catch (error) {
            setFetchError(error.message)
            setFetchStatus('failed');
        }
        handleDeleteImageInput();
    }

    // handle succeded overlay
    const [isOverlay, setIsOverlay] = useState(false);
    const handleOverlay = () => {
        setIsOverlay(true);
        setTimeout(() => {
            setIsOverlay(false)
        }, 3000);
    }

    // Gestisco il tasto 'riprova' in caso di errore nel caricamento dell'immagine
    const handleUploadRetry = () => {
        setFetchStatus('idle');
        setFetchError(null);
    }

    // Gestisco l'eliminazione dell'immagine dall'input
    const handleDeleteImageInput = () => {
        setUploadedImage(null);
        inputEmpty();
    }

    // Gestisco l'eliminazione della foto dal server
    const [deleteFetchStatus, setDeleteFetchStatus] = useState('idle');
    const handleDeleteImage = async (event) => {
        const { id } = event.target;
        setDeleteFetchStatus('loading');

        // mando al server l'id dell'immagine da eliminare
        const photoPublicId = inputDataGallery.find(element => element.public_id === id).public_id;

        try {
            const token = localStorage.getItem('ticino-wild-token');
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/delete-image`;
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const options = {
                method: 'DELETE',
                headers,
                body: JSON.stringify({ public_id: photoPublicId })
            }
            const response = await fetch(url, options);
            if (response.ok) {
                // cancello l'immagine da inputData.gallery
                let oldGallery = [...inputDataGallery];
                let newGallery = oldGallery.filter(element => element.public_id !== id);
                setInputData(prevState => ({
                    ...prevState,
                    gallery: newGallery
                }))
                // Elimino la copertina se corrisponde alla foto eliminata
                if (photoPublicId === coverPublicId) {
                    setInputData(prevState => ({
                        ...prevState,
                        coverImageUrl: null
                    }))
                }
                //
                setDeleteFetchStatus('succeeded');
            } else {
                setDeleteFetchStatus('failed');
            }
        } catch (error) {
            setDeleteFetchStatus('failed');
        }
    }

    return (

        <div className="flex flex-col p-2 gap-2 w-full lg:w-fit">

            {/* Anteprima foto inviate al server */}
            <div className="flex flex-col gap-2 bg-white p-2 rounded-xl">
                {inputDataGallery && inputDataGallery.length === 0 && <h3 className="text-neutral-400 italic">Nessun immagine caricata</h3>}
                {inputDataGallery && inputDataGallery.length > 0 && <h3>Foto caricate:</h3>}
                <div>
                    {deleteFetchStatus === 'loading' && <div className="absolute top-0 right-0 bottom-0 left-0 bg-white opacity-80 z-10"></div>}
                    {
                        inputDataGallery && inputDataGallery.length > 0 &&
                        <ReorderModal inputDataGallery={inputDataGallery} setInputData={setInputData} coverPublicId={coverPublicId} handleSelectCover={handleSelectCover} handleDeleteImage={handleDeleteImage} />
                    }
                </div>
                <h5 className="italic text-xs">(Clicca su una foto per impostarla come copertina)</h5>
                <h5 className="italic text-xs">(Trascina i blocchi per riordinare le foto della galleria)</h5>
            </div>

            {/* Uploader */}
            <motion.div
                onDragOver={(e) => { e.preventDefault() }}
                whileHover={!uploadedImage && { backgroundColor: 'rgba(0, 0, 0, 0.2)' }}
                onDrop={handleUploadImage}
                dragConstraints={{ top: 0, left: 0, bottom: 0, right: 0 }}
            >
                <div className="flex flex-col items-center justify-center w-full lg:w-[800px] lg:h-[440px] border-[4px] border-dashed border-[#225424] rounded-xl p-8 text-center my-8 sm:h-[484] relative">
                    {/* Input nascosto */}
                    <input ref={fileInputRef} type="file" accept="image/" id="fileInput" onChange={handleUploadImage} style={{ display: 'none' }} />

                    {/* Succeeded overlay */}
                    {
                        isOverlay &&
                        <div className="absolute top-0 right-0 bottom-0 left-0 bg-neutral-200 rounded-lg opacity-90 flex flex-col items-center justify-center gap-2">
                            <i className="fi fi-br-check text-4xl text-[#225424]"></i>
                            <h3>Foto caricata!</h3>
                        </div>
                    }

                    {
                        uploadedImage && (fetchStatus === 'idle' || fetchStatus === 'succeeded') &&
                        /* Anteprima */
                        <div className="p-2 border-2 rounded flex flex-col items-center lg:flex-rows gap-4">
                            <h4>Vuoi caricare questa foto?</h4>
                            <div className="relative text-xs"><img src={uploadedImage} alt="" className="w-48" /><i className="fi fi-sr-circle-xmark absolute top-2 right-2 text-3xl cursor-pointer" onClick={handleDeleteImageInput}></i> </div>
                            {!isSizeError && <GreenButton text="Carica immagine" click={sendImage} />}
                        </div>
                    }
                    {
                        uploadedImage && fetchStatus === 'loading' &&
                        <Loader />
                    }
                    {
                        fetchStatus === 'failed' &&
                        <div className="flex flex-col gap-2">
                            <h4>{fetchError}</h4>
                            <NoBgButton text="Riprova" click={handleUploadRetry} />
                        </div>
                    }
                    {
                        !uploadedImage && (fetchStatus === 'idle' || fetchStatus === 'succeeded') &&
                        <div className="flex flex-col gap-2 items-center">
                            <img src={uploadIcon} alt="upload" className="w-32" />
                            <h2 className="text-neutral-500">Drag & Drop</h2>
                            <p>oppure</p>
                            {!isImageLoading && !isSizeError && <div className="my-8"><GreenButton text="Seleziona file" icon={<i className="fi fi-br-cloud-upload text-2xl"></i>} click={() => fileInputRef.current.click()} /></div>}
                            {isSizeError && <h4 className="text-red-500">L'immagine supera le dimensioni consentite</h4>}
                            <p>
                                La dimensione massima consentita per i file è di 10 MB.
                                Sono consentiti files di tipo 'jpg' e 'png'
                            </p>
                        </div>

                    }
                    {isImageLoading && !isSizeError && <Loader />}
                </div>
            </motion.div>

        </div >
    )
}

export default UploadPhotos;